import { useCallback, useState } from "react";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "./useAPI";
import { useError } from "./useError";
import { useLoading } from "./useLoading";
import { useRoleContext } from "./useRoleContext";

import type { CustomerType } from "../components/SupplierNetwork/types";

export function useRemoveSupplierCustomer() {
  const { api } = useAPI();
  const { role } = useRoleContext();

  if (role && role.type !== "SHIPPER") {
    throw new Error(
      "useRemoveSupplierCustomer can only be used by shipper users",
    );
  }

  const { error, setError } = useError();
  const { loading, setLoading } = useLoading(false);
  const [success, setSuccess] = useState<boolean>(false);

  const resetState = () => {
    setError(null);
    setLoading(false);
    setSuccess(false);
  };

  const removeCustomer = useCallback(
    (customerId: number, customerType: CustomerType) => {
      if (!role) return;

      setError(null);
      setLoading(true);

      const url =
        customerType === "INTERNAL"
          ? `/supplier-network-internal-customers/${customerId}`
          : `/supplier-network-external-customers/${customerId}`;

      api("delete", url)
        .then(() => {
          setSuccess(true);
          setError(null);
        })
        .catch((e) => {
          setError(
            extractAPIRequestErrorMessage(
              e,
              "Failed to remove supplier customer",
            ),
          );
          setSuccess(false);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [api, role, setError, setLoading],
  );

  return {
    loading,
    error,
    success,
    removeCustomer,
    resetState,
  };
}
